import { Interface } from "@ethersproject/abi"
import type { Pod } from "@orcaprotocol/orca-sdk"
import invariant from "tiny-invariant"
import type { AbiInput, AbiItem } from "web3-utils"

import type { TallyActionArgs } from "createProposal/action/types"
import TallyAction from "createProposal/action/TallyAction"
import { ExecutableCallType } from "query/graphql"
import { jsonStringify } from "common/helpers/serialization"
import { getInputTypesAndValues } from "createProposal/helpers/action"

const mintAddressInput: AbiInput = {
  internalType: "address[]",
  name: "_accounts",
  type: "address[]",
}

const idInput: AbiInput = {
  internalType: "uint256",
  name: "_id",
  type: "uint256",
}

const dataInput: AbiInput = {
  internalType: "bytes",
  name: "data",
  type: "bytes",
}

const burnAddressInput: AbiInput = {
  internalType: "address[]",
  name: "_accounts",
  type: "address[]",
}

const mintSingleBatchMethod: AbiItem = {
  constant: false,
  inputs: [mintAddressInput, idInput, dataInput],
  name: "mintSingleBatch",
  outputs: [],
  payable: false,
  stateMutability: "nonpayable",
  type: "function",
}

const burnSingleBatchMethod: AbiItem = {
  constant: false,
  inputs: [burnAddressInput, idInput],
  name: "burnSingleBatch",
  outputs: [],
  payable: false,
  stateMutability: "nonpayable",
  type: "function",
}

class ManageOrcaPodAction extends TallyAction {
  addedMembers: string[]
  removedMembers: string[]
  addedMember: string
  pod: Pod | undefined

  constructor(args?: TallyActionArgs) {
    super(args)

    this.recipe = ExecutableCallType.Orcamanagepod
    this.addedMembers = []
    this.removedMembers = []
    this.pod = undefined
    this.addedMember = ""
  }

  getCalldata(): string | string[] {
    const { addedMembers, pod, removedMembers } = this

    invariant(pod, "`pod` should be defined")

    const idInputWithValue = {
      ...idInput,
      value: pod.id.toString(),
    }

    const dataInputWithValue = {
      ...dataInput,
      value: "0x",
    }

    const mintAddressInputWithValue = {
      ...mintAddressInput,
      value: jsonStringify(addedMembers),
    }

    const burnAddressInputWithValue = {
      ...burnAddressInput,
      value: jsonStringify(removedMembers),
    }

    const mintInputs = [
      mintAddressInputWithValue,
      idInputWithValue,
      dataInputWithValue,
    ]
    const burnInputs = [burnAddressInputWithValue, idInputWithValue]

    const { values: mintValues } = getInputTypesAndValues(mintInputs)
    const { values: burnValues } = getInputTypesAndValues(burnInputs)

    const contractInterface = new Interface(
      jsonStringify([mintSingleBatchMethod, burnSingleBatchMethod]),
    )
    const encodedMintForOz = contractInterface.encodeFunctionData(
      "mintSingleBatch",
      mintValues,
    )
    const encodedBurnForOz = contractInterface.encodeFunctionData(
      "burnSingleBatch",
      burnValues,
    )

    const shouldMint = addedMembers.length > 0
    const shouldBurn = removedMembers.length > 0
    const shouldMintAndBurn = shouldMint && shouldBurn

    if (shouldMintAndBurn) {
      return [encodedMintForOz, encodedBurnForOz]
    }

    if (shouldMint) {
      return encodedMintForOz
    }

    return encodedBurnForOz
  }

  getSignature(): string | string[] {
    const { addedMembers, removedMembers } = this

    const shouldMint = addedMembers.length > 0
    const shouldBurn = removedMembers.length > 0
    const shouldMintAndBurn = shouldMint && shouldBurn

    const mintSignature = "mintSingleBatch(address[],uint256,bytes)"
    const burnSignature = "burnSingleBatch(address[],uint256)"

    if (shouldMintAndBurn) return [mintSignature, burnSignature]

    const signature = shouldMint ? mintSignature : burnSignature

    return signature
  }
}

export default ManageOrcaPodAction
